import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from '../ui/scroll-area';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { MessageSquare, Users, Ban, MoreVertical, ShieldOff, UserMinus } from 'lucide-react';
import {
  useRotationHealth,
  HEALTH_RING_CLASS,
} from '../../hooks/useRotationHealth';
import { CadenceBadge } from '../CadenceBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One contact row — extracted for the same reason as ConversationRow:
 * `useRotationHealth` is a hook and must live at component top-level,
 * not inside a `.map()` callback.
 */
const ContactRow = ({ contact, isBlocked, onStartChat, block, unblock, onRemove }) => {
  const health = useRotationHealth(contact.id);
  const ringClass = health ? HEALTH_RING_CLASS[health] : '';

  return (
    <div
      className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-all duration-200 ${
        isBlocked ? 'opacity-60' : ''
      }`}
      data-testid={`contact-row-${contact.id}`}
    >
      <button
        onClick={() => onStartChat(contact)}
        className="flex items-center gap-3 flex-1 text-left"
        data-testid={`contact-${contact.id}`}
      >
        <div className="relative">
          <Avatar
            className={`h-11 w-11 ${ringClass} ring-offset-2 ring-offset-card`}
            data-testid={health ? `avatar-health-${health}` : undefined}
          >
            <AvatarFallback className="bg-secondary text-slate-300">
              {contact.username[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-card ${
              contact.is_online ? 'bg-emerald-500' : 'bg-slate-500'
            }`}
          />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-medium text-white flex items-center gap-1.5 truncate">
            <span className="truncate">{contact.username}</span>
            <CadenceBadge userId={contact.id} />
            {isBlocked && (
              <span
                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30"
                data-testid={`blocked-pill-${contact.id}`}
              >
                <Ban className="w-3 h-3" /> Blocked
              </span>
            )}
          </p>
          <p className="text-xs text-slate-500">
            {contact.is_online ? 'Online' : 'Offline'}
          </p>
        </div>
        <MessageSquare className="w-4 h-4 text-slate-400 shrink-0" />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="p-1.5 rounded hover:bg-secondary/70 text-slate-400 shrink-0"
            data-testid={`contact-menu-${contact.id}`}
            aria-label={`Options for ${contact.username}`}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isBlocked ? (
            <DropdownMenuItem
              onClick={() => unblock(contact)}
              data-testid={`contact-unblock-${contact.id}`}
            >
              <ShieldOff className="w-4 h-4 mr-2" />
              Unblock
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => block(contact)}
              className="text-red-400 focus:text-red-300"
              data-testid={`contact-block-${contact.id}`}
            >
              <Ban className="w-4 h-4 mr-2" />
              Block
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onRemove(contact)}
            className="text-red-400 focus:text-red-300"
            data-testid={`contact-remove-${contact.id}`}
          >
            <UserMinus className="w-4 h-4 mr-2" />
            Remove contact
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const ContactList = ({ contacts, onStartChat, onContactRemoved }) => {
  // Locally-mirrored blocklist. We refetch on mount so the UI matches the
  // server even after a multi-device unblock. Updates are optimistic with a
  // toast rollback if the server rejects.
  const [blocked, setBlocked] = useState(new Set());
  // The contact pending a confirmation dialog before we hard-delete the
  // mutual link + the direct conversation. We keep this hoisted in the
  // list (not the row) so closing the menu doesn't unmount the dialog.
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const reload = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/contacts/blocked`);
      setBlocked(new Set(res.data || []));
    } catch {}
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const block = async (contact) => {
    const prev = new Set(blocked);
    setBlocked((s) => new Set(s).add(contact.id));
    try {
      await axios.post(`${API}/contacts/${contact.id}/block`);
      toast.success(`Blocked ${contact.username}`);
    } catch (e) {
      setBlocked(prev);
      toast.error(e?.response?.data?.detail || 'Block failed');
    }
  };

  const unblock = async (contact) => {
    const prev = new Set(blocked);
    setBlocked((s) => {
      const next = new Set(s);
      next.delete(contact.id);
      return next;
    });
    try {
      await axios.post(`${API}/contacts/${contact.id}/unblock`);
      toast.success(`Unblocked ${contact.username}`);
    } catch (e) {
      setBlocked(prev);
      toast.error('Unblock failed');
    }
  };

  const performRemove = async (contact) => {
    if (!contact || removing) return;
    setRemoving(true);
    try {
      const res = await axios.delete(`${API}/contacts/${contact.id}`);
      const msgCount = res.data?.deleted_messages || 0;
      toast.success(
        `Removed ${contact.username}${
          msgCount ? ` · cleared ${msgCount} message${msgCount === 1 ? '' : 's'}` : ''
        }`,
      );
      // Tell the parent so the contact list + conversation list refresh.
      onContactRemoved?.(contact);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    } finally {
      setRemoving(false);
      setRemoveTarget(null);
    }
  };

  return (
    <div className="flex-1 overflow-hidden">
      <ScrollArea className="h-full">
        <div className="p-3 space-y-1">
          {contacts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No contacts yet</p>
              <p className="text-sm mt-1">Use invite codes to add contacts</p>
            </div>
          ) : (
            contacts.map((contact) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                isBlocked={blocked.has(contact.id)}
                onStartChat={onStartChat}
                block={block}
                unblock={unblock}
                onRemove={setRemoveTarget}
              />
            ))
          )}
        </div>
      </ScrollArea>

      <AlertDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && !removing && setRemoveTarget(null)}
      >
        <AlertDialogContent
          className="bg-card border-border"
          data-testid="contact-remove-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              Remove {removeTarget?.username || 'this contact'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This unlinks you from each other's contact lists and{' '}
              <span className="text-red-400 font-semibold">
                deletes your direct chat with them
              </span>{' '}
              along with every message in it. Group conversations that
              include both of you are not affected. To re-establish the
              link later, you'll need to add them again by contact tag.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={removing}
              data-testid="contact-remove-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (removeTarget) performRemove(removeTarget);
              }}
              disabled={removing}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="contact-remove-confirm"
            >
              {removing ? 'Removing…' : 'Remove and delete chat'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
